<template>

  <DefaultCrudView
    @create-new="newRoutine"
    :breadcrumbsItems="breadcrumbs"
    :actions="crudActions"
    containerLarge
  >
    <template slot="content">

      <v-data-table
        :headers="headers"
        :items="routines"
        class="elevation-1"
        :loading="loading"
        :options.sync="options"
        :server-items-length="itemsLength"
        :footer-props="$defaultDataTableFooterProps"
      >
        <template v-slot:item.global="{ item }">
          <v-icon v-if="item.global" color="success">
            mdi-check
          </v-icon>
          <v-icon v-else color="error">
            mdi-close
          </v-icon>
        </template>

        <template v-slot:item.routine_form_spec="{ item }">
          <span v-if="item.routine_form_spec">
            {{ item.routine_form_spec.substring(0, 50) }}{{ item.routine_form_spec.length > 50 ? '...' : '' }}
          </span>
          <span v-else>-</span>
        </template>

        <template v-slot:item.routine_execute="{ item }">
          <span v-if="item.routine_execute">
            {{ item.routine_execute.substring(0, 50) }}{{ item.routine_execute.length > 50 ? '...' : '' }}
          </span>
          <span v-else>-</span>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-tooltip v-if="canManageRoutine(item)" left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                class="mr-2"
                @click="editRoutine(item)"
              >
                mdi-pencil
              </v-icon>
            </template>
            <span>{{ $t('edit') }}</span>
          </v-tooltip>

          <v-tooltip v-if="canManageRoutine(item)" left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                class="mr-2"
                @click.stop="deleteRoutine(item)"
              >
                mdi-delete
              </v-icon>
            </template>
            <span>{{ $t('delete') }}</span>
          </v-tooltip>

          <v-tooltip v-if="canManageRoutine(item)" left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                class="mr-2"
                @click="openPipeStep(item)"
              >
                mdi-tune-vertical
              </v-icon>
            </template>
            <span>{{ $t('manage_pipesteps') }}</span>
          </v-tooltip>

          <v-tooltip v-if="canManageRoutine(item)" left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                class="mr-2"
                @click="openServerDialog(item)"
              >
                mdi-server-network
              </v-icon>
            </template>
            <span>{{ $t('manage_routine_servers') }}</span>
          </v-tooltip>
        </template>

      </v-data-table>

      <DefaultModal 
        :title="getModalTitle()" 
        ref="modal" 
        v-on:submit="save"
      >
        <template v-slot:content>
          <v-form ref="form" v-model="valid">
            <DynamicFormContent :fields="fields"></DynamicFormContent>
          </v-form>
        </template>
      </DefaultModal>

      <DefaultModal
        :title="$t('routine_servers')"
        ref="serverModal"
        @submit="saveServerLinks"
      >
        <template v-slot:content>
          <v-select
            v-model="selectedServerIds"
            :items="allServers"
            item-text="server_key"
            item-value="server_id"
            :label="$t('servers')"
            multiple
            chips
            outlined
          />
        </template>
      </DefaultModal>

      <!-- Confirmation Dialog -->
      <v-dialog v-model="confirmDialog" max-width="400">
        <v-card>
          <v-card-title class="headline">{{ $t('confirm') }}</v-card-title>
          <v-card-text>
            {{ $t('confirm_delete') }}
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" text @click="confirmDialog = false">
              {{ $t('close') }}
            </v-btn>
            <v-btn color="red darken-1" text @click="confirmDelete">
              {{ $t('delete') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

    </template>
  
  </DefaultCrudView>
</template>

<style scoped>
  .container {
    padding: 0;
  }

  .table-container {
    margin-top: 20px;
  }
</style>

<script>

import { mapActions, mapGetters } from 'vuex'

import DefaultCrudView from '@/components/DefaultCrudView.vue'
import DefaultModal from '@/components/DefaultModal.vue'
import DynamicFormContent from '@/components/DynamicFormContent.vue'
import AppActions from '@/store/app/actions-types'
import routineApi from '@/services/api/pipeline/routine'
import serverApi from '@/services/api/pipeline/server'
import routineServerApi from '@/services/api/pipeline/routineserver'
import UserRoles from '@/store/core/user/user-roles'

export default {
  name: "Routine",

  components: {
    DefaultCrudView,
    DefaultModal,
    DynamicFormContent
  },

  computed: {
    ...mapGetters('app', [
      'getIconForOperationSettingsItem'
    ]),

    ...mapGetters('core/auth', [
      'checkAccessRights'
    ]),

    fields() {
        return [
          {
            name: 'routine_key',
            type: 'text',
            label: this.$t('routine_key'),
            required: true,
            value: this.selectedRoutine?.routine_key || ''
          },
          {
            name: 'routine_form_spec',
            type: 'textarea',
            label: this.$t('routine_form_spec'),
            rows: 4,
            value: this.selectedRoutine?.routine_form_spec || ''
          },
          {
            name: 'routine_execute',
            type: 'textarea',
            label: this.$t('routine_execute'),
            rows: 4,
            value: this.selectedRoutine?.routine_execute || ''
          }
        ]
    },

    breadcrumbs() {
      return [
        {
          text: this.$t('routines'),
          prependIcon: this.getIconForOperationSettingsItem('routines'),
        },
      ]
    }
  },

  data() {
    return {
      loading: true,
      routines: [],
      options: {
        sortBy: ['routine_key'],
        sortDesc: [false],
      },
  itemsLength: 0,
  confirmDialog: false,
  valid: false,
  selectedRoutine: null,
  selectedServerRoutine: null,
  allServers: [],
  attachedServerIds: [],
  selectedServerIds: [],
  crudActions: [
        {
          text: "create_routine",
          eventToEmit: "create-new",
        }
      ],
      headers: [
        {
          text: this.$t('routine_id'),
          align: 'start',
          sortable: true,
          value: 'routine_id',
        },
        {
          text: this.$t('global'),
          value: 'global',
          sortable: true,
        },
        {
          text: this.$t('routine_key'),
          value: 'routine_key',
          sortable: true,
        },
        {
          text: this.$t('routine_form_spec'),
          value: 'routine_form_spec',
          sortable: false,
        },
        {
          text: this.$t('routine_execute'),
          value: 'routine_execute',
          sortable: false,
        },
        {
          text: this.$t('actions'),
          value: 'actions',
          sortable: false,
        },
      ]
    }
  },

  mounted() {
    this.reloadData()
  },

  watch: {
    options: {
      handler () {
        this.reloadData()
      },
      deep: true,
    },
  },

  methods: {
      openPipeStep(item) {
        if (!this.canManageRoutine(item)) {
          return
        }

        this.$router.push({
          name: 'PipeStep',
          query: { routine_id: item.routine_id }
        })
      },
    ...mapActions('app', [
      AppActions.OPEN_APP_SUCCESS_MESSAGE,
      AppActions.OPEN_APP_ERROR_MESSAGE
    ]),

    newRoutine() {
      this.selectedRoutine = null
      this.$refs.modal.openModal()
    },

    save() {
      if (!this.$refs.form.validate()) {
        return
      }

      this.$refs.modal.errorMessage = null
      this.$refs.modal.errorList = []

      const routine = this.fields.reduce((data, field) => {
        data[field.name] = field.value
        return data
      }, {
        global: this.checkIsMaster()
      })

      let apiCall
      if (this.selectedRoutine) {
        apiCall = routineApi.updateRoutine(this.selectedRoutine.routine_id, routine)
      } else {
        apiCall = routineApi.createRoutine(routine)
      }

      apiCall
        .then(() => {
          let message = this.selectedRoutine ? 'Routine updated' : 'Routine created'
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t(message))
          this.$refs.modal.closeModal()
          this.reloadData()
        })
        .catch((error) => {
          console.error('Error saving routine:', error)
          this[AppActions.OPEN_APP_ERROR_MESSAGE](error.response?.data?.message || 'Error saving routine')
          this.$refs.modal.submitting = false
        })
    },

    reloadData() {
      let options = {...this.$route.query}

      if (this.options.sortBy.length > 0) {
        options.order = this.options.sortBy[0]
      }

      if (this.options.sortDesc.length > 0 && this.options.sortDesc[0]) {
        options.direction = 'DESC'
      }

      options.limit = this.options.itemsPerPage
      options.offset = this.options.itemsPerPage * (this.options.page - 1)
      this.loading = true
      
      routineApi.getAllRoutines(options)
        .then((response) => {
          const result = response.data
          this.routines = result.items || result.data || result || []
          this.itemsLength = result.total || result.count || (result.items ? result.items.length : 0)
          this.loading = false
        })
        .catch((error) => {
          console.error('Error loading routines:', error)
          this[AppActions.OPEN_APP_ERROR_MESSAGE]('Error loading routines')
          this.loading = false
        })
    },

    editRoutine(routine) {
      if (!this.canManageRoutine(routine)) {
        return
      }

      this.selectedRoutine = routine
      this.$refs.modal.openModal()
    },

    async openServerDialog(routine) {
      if (!this.canManageRoutine(routine)) {
        return
      }

      this.selectedServerRoutine = routine
      this.loading = true

      try {
        const [allResponse, attachedResponse] = await Promise.all([
          serverApi.getAllServers({ order: 'server_key', direction: 'ASC', limit: 200, offset: 0 }),
          serverApi.getServersByRoutine(routine.routine_id),
        ])
        const all = allResponse.data
        const attached = attachedResponse.data
        this.allServers = all.items || all.data || all || []
        this.attachedServerIds = (attached.items || attached.data || attached || []).map(server => server.server_id)
        this.selectedServerIds = [...this.attachedServerIds]
        this.$refs.serverModal.openModal()
      } catch (error) {
        this[AppActions.OPEN_APP_ERROR_MESSAGE]('Error loading routine servers')
      } finally {
        this.loading = false
      }
    },

    async saveServerLinks() {
      const routineId = this.selectedServerRoutine.routine_id
      const selected = this.selectedServerIds.map(Number)
      const attached = this.attachedServerIds.map(Number)
      const toCreate = selected.filter(serverId => !attached.includes(serverId))
      const toDelete = attached.filter(serverId => !selected.includes(serverId))

      try {
        await Promise.all([
          ...toCreate.map(serverId => routineServerApi.createRoutineServer(serverId, routineId)),
          ...toDelete.map(serverId => routineServerApi.deleteRoutineServer(serverId, routineId)),
        ])
        this.$refs.serverModal.closeModal()
        this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('save_successful'))
      } catch (error) {
        this[AppActions.OPEN_APP_ERROR_MESSAGE](error.response?.data?.message || 'Error saving routine servers')
      } finally {
        this.$refs.serverModal.submitting = false
      }
    },

    deleteRoutine(routine) {
      if (!this.canManageRoutine(routine)) {
        return
      }

      this.routineToDelete = routine
      this.confirmDialog = true
    },

    confirmDelete() {
      if (this.routineToDelete && this.canManageRoutine(this.routineToDelete)) {
        routineApi.deleteRoutine(this.routineToDelete.routine_id)
          .then(() => {
            this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('Routine deleted'))
            this.reloadData()
            this.confirmDialog = false
            this.routineToDelete = null
          })
          .catch((error) => {
            console.error('Error deleting routine:', error)
            this[AppActions.OPEN_APP_ERROR_MESSAGE]('Error deleting routine')
            this.confirmDialog = false
            this.routineToDelete = null
          })
      }
    },

    checkIsMaster() {
      return this.checkAccessRights([UserRoles.USER_MASTER])
    },

    checkUserIsAdmin() {
      return this.checkAccessRights([UserRoles.USER_ADMIN])
    },

    canManageRoutine(routine) {
      const hasTenant = routine?.tenant_id !== null && routine?.tenant_id !== undefined

      return this.checkIsMaster()
        ? !hasTenant
        : this.checkUserIsAdmin() && hasTenant
    },

    getModalTitle() {
      return this.selectedRoutine ? this.$t('edit_routine') : this.$t('new_routine')
    }
  }
}

</script>
